import { AnalyticIdentifier } from '@common/neo.tax';
import {
  Button,
  ModalActions,
  ModalHeader,
  Stack,
  Typography,
  getRecipeColor,
  InputFormContainer,
  ControlledTextArea,
} from '@ui-components/neo.tax';
import { ReactComponent as LaptopFlash } from '@ui-components/neo.tax/dist/icons/laptop-flash.svg';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useUpdateRdProject, RdProject } from '@neo.tax/rd/hooks';
import { useAnalyticId, ProjectEligibilityModal, HIGHLIGHT_VARIANT } from '@neo.tax/shared';

interface RdProjectTechnicalChallengesModalProps {
  isOpen: boolean;
  onClose: () => void;
  rdProject: RdProject;
}

interface RdProjectTechnicalChallengesFormProps {
  technicalChallenges: string;
}

export const RdProjectTechnicalChallengesModal = ({
  isOpen,
  onClose,
  rdProject,
}: RdProjectTechnicalChallengesModalProps): React.ReactElement => {
  const [updateRdProject] = useUpdateRdProject({ rdProject, includeOptimisticResponse: true });
  const { control, handleSubmit, reset } = useForm<RdProjectTechnicalChallengesFormProps>({
    mode: 'onSubmit',
    defaultValues: {
      technicalChallenges: rdProject.technicalChallenges ?? '',
    },
  });

  const technicalChallengesInputId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_PROJECT_TECHNICAL_CHALLENGES);

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<RdProjectTechnicalChallengesFormProps> = async (data) => {
    await updateRdProject(data);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      reset({
        technicalChallenges: rdProject.technicalChallenges ?? '',
      });
    }
  }, [isOpen, rdProject.technicalChallenges, reset]);

  return (
    <ProjectEligibilityModal isOpen={isOpen}>
      <Stack as="form" direction="column" gap={8} onSubmit={handleSubmit(onSubmit)} paddingX={4}>
        <ModalHeader
          icon={<LaptopFlash width={24} color={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').text} />}
          iconBgColor={getRecipeColor(HIGHLIGHT_VARIANT, 'colorBg').bg}
          title={
            <Typography fontSize="base" fontWeight="semibold">
              Project{' '}
              <Typography
                fontSize="inherit"
                fontWeight="inherit"
                color={getRecipeColor(HIGHLIGHT_VARIANT, 'whiteBg').text}
              >
                {rdProject.name}
              </Typography>{' '}
              Technical Challenges
            </Typography>
          }
          copy="Projects must involve eliminating some technical uncertainty to qualify"
        />

        <InputFormContainer
          label="What were the main technical challenges or uncertainties in the technical design at the beginning of the project? Describe the 2-4 main technical challenges/uncertainties in the form of a question with supporting detail about why it was a technical challenge and how the project set out to eliminate those technical uncertainties."
          inputId={technicalChallengesInputId}
        >
          <ControlledTextArea
            control={control}
            name="technicalChallenges"
            id={technicalChallengesInputId}
            placeholder="1-2 paragraphs or a list of items"
            rows={8}
          />
        </InputFormContainer>
        <ModalActions hasTopBorder={false}>
          <Button variant="white" rounded="regular" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" rounded="regular" size="base" type="submit">
            Save
          </Button>
        </ModalActions>
      </Stack>
    </ProjectEligibilityModal>
  );
};
